import { useEffect, useState } from 'react';
import runningIconUrl from '../../assets/icons/running.svg?url';
import { i18nService } from '../../services/i18n';

/** 执行状态只取本轮消息事实；终止后不能继续保留加载动画。 */
export type ConversationExecutionPhase = 'idle' | 'thinking' | 'waiting' | 'running';

/** 根据本轮是否运行及正文、思考、工具事件确定展示状态，不依赖上一轮内容。 */
export function conversationExecutionPhase(facts: {
  running: boolean;
  hasAnswer: boolean;
  hasThinking: boolean;
  hasTools: boolean;
}): ConversationExecutionPhase {
  if (!facts.running) return 'idle';
  if (facts.hasAnswer) return 'running';
  return facts.hasThinking || facts.hasTools ? 'waiting' : 'thinking';
}

/** 对齐客户端：起手显示思考中，工具/思考后等待正文使用原始运行图标，已有正文时显示续行圆点。 */
export default function ConversationExecutionStatus({ phase, activityLabel, startedAt }: {
  phase: ConversationExecutionPhase;
  /** 当前未完成工具的摘要，缺失时使用等待模型文案。 */
  activityLabel?: string;
  /** 本轮主用户消息的服务端毫秒时间戳；刷新页面后计时不重置。 */
  startedAt?: number;
}) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (phase === 'idle') return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [phase, startedAt]);
  const seconds = startedAt && Number.isFinite(startedAt) ? Math.max(0, Math.floor((now - startedAt) / 1000)) : null;
  const elapsed = seconds === null ? null : <span className="ml-2 shrink-0 font-mono text-xs tabular-nums opacity-70" aria-live="off">
    {seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`}
  </span>;
  if (phase === 'idle') return null;
  if (phase === 'thinking') return <div className="pb-0.5 text-sm leading-[1.6] text-claude-textSecondary/70 dark:text-claude-darkTextSecondary/70" role="status" aria-live="polite">
    {i18nService.t('coworkThinking')}{elapsed}
  </div>;
  if (phase === 'waiting') return <div className="flex items-center gap-2 py-1.5" role="status" aria-live="polite">
    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true"><img src={runningIconUrl} className="h-5 w-5" alt="" /></span>
    <span className="text-xs text-claude-textSecondary/75 dark:text-claude-darkTextSecondary/75">{activityLabel ?? i18nService.t('coworkActivityAwaitingModel')}</span>
    {elapsed}
  </div>;
  return <div className="flex items-center gap-1 py-1.5 pl-1" role="status" aria-label={i18nService.t('coworkStatusRunning')}>
    {[0, 1, 2].map(index => <span key={index}
      className="h-1.5 w-1.5 animate-bounce rounded-full bg-claude-textSecondary/50 dark:bg-claude-darkTextSecondary/50 motion-reduce:animate-none"
      style={{ animationDelay: `${index * 150}ms`, animationDuration: '1s' }} />)}
    {elapsed}
  </div>;
}
